import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { MAX_TILE_ZOOM, fetchMapTile } from '@/lib/mapTiles';

const DEFAULT_ZOOM = 16;

/** How far one arrow-key press moves the pin; about a metre, ten with Shift. */
const NUDGE_DEGREES = 0.00001;

/** A simple pin, inline — sidesteps Leaflet's default marker images, which need their own asset-path fix under Vite. */
const PIN_SVG = `
  <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2C8.5 2 4 6.5 4 12c0 7 10 14 10 14s10-7 10-14c0-5.5-4.5-10-10-10z"
      fill="var(--color-primary, #2563eb)" stroke="white" stroke-width="1.5" />
    <circle cx="14" cy="12" r="3.5" fill="white" />
  </svg>
`;

type TileImage = HTMLImageElement & { blobUrl?: string; abort?: AbortController };

/**
 * Authenticated tiles (M1-26): a plain Leaflet `TileLayer` sets `<img src>`
 * directly, which cannot carry an `Authorization` header, and this API has no
 * other way to authenticate. This `GridLayer` subclass fetches each tile
 * itself (`fetchMapTile`, the same bearer token every other call uses) and
 * turns the response into a blob URL — the standard workaround for an
 * authenticated tile source. `tileunload` is what revokes the blob URL and
 * aborts an in-flight fetch; without it every pan leaks one blob per tile.
 */
class AuthenticatedTileLayer extends L.GridLayer {
  createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const tile = document.createElement('img') as TileImage;
    const controller = new AbortController();
    tile.abort = controller;

    fetchMapTile(coords.z, coords.x, coords.y, controller.signal)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        tile.blobUrl = url;
        tile.src = url;
        done(undefined, tile);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        done(error instanceof Error ? error : new Error('Tile fetch failed'), tile);
      });

    return tile;
  }
}

export interface PropertyLocationMapProps {
  latitude: number;
  longitude: number;
  /** Whether the marker may be dragged to correct the pin. */
  editable: boolean;
  /** Called with the dropped position; rejecting reverts the marker. */
  onCorrect?: (latitude: number, longitude: number) => Promise<unknown>;
  /** The read-only mini-map reused from the job screen (the AC's own words): smaller, no pan/zoom chrome. */
  compact?: boolean;
  /**
   * Starting zoom (M1-36). Defaults to street level, which is right when the
   * pin IS the property.
   *
   * ⚠️ When the pin is only a starting point — a town centroid the office is
   * about to drag onto a rural driveway — street zoom would present a guess
   * with the confidence of an answer, and leave the property off-screen. The
   * caller passes a zoom matching how precisely it actually knows where to
   * look.
   */
  zoom?: number;
}

/**
 * The map the Location block draws (M1-23's own forward-reference, M1-26).
 * Renders nothing about WHY there is or isn't a pin — `PropertyDetail`'s
 * existing text already says that in words, per web/CONVENTIONS.md's "never
 * colour-only" rule. This component only draws the pin it is given and, when
 * `editable`, reports where it was dragged to.
 */
export function PropertyLocationMap({
  latitude,
  longitude,
  editable,
  onCorrect,
  compact = false,
  zoom = DEFAULT_ZOOM,
}: PropertyLocationMapProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // Held in refs, NOT in the effect's dependency array. `onCorrect` is an
  // inline arrow at every call site, so a new identity arrives on every parent
  // render — including the one `mutateAsync` itself triggers the instant a drag
  // lands. As a dependency it tore the whole map down mid-save and rebuilt it
  // at the pre-drag coordinates, which read as the pin snapping back. `t` is
  // the same story on any i18n re-render. The map is rebuilt only for a real
  // coordinate or mode change; these two are read through the ref instead.
  const onCorrectRef = useRef(onCorrect);
  onCorrectRef.current = onCorrect;
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    setUnavailable(false);
    let hasLoadedATile = false;

    const map = L.map(container, {
      center: [latitude, longitude],
      zoom,
      // The proxy 400s past this, and Leaflet would otherwise let the user
      // zoom for ever (see MAX_TILE_ZOOM) into a grey square.
      maxZoom: MAX_TILE_ZOOM,
      zoomControl: !compact,
      attributionControl: false,
      dragging: !compact,
      scrollWheelZoom: false,
      doubleClickZoom: !compact,
      boxZoom: !compact,
      keyboard: !compact,
    });

    const tiles = new AuthenticatedTileLayer({ tileSize: 256, maxZoom: MAX_TILE_ZOOM });
    // A tile arriving proves the provider and the account are up, so the
    // fallback below is retracted if it ever went up: tiles resolve
    // concurrently and in any order, and one transient failure landing before
    // the first success must not leave "map unavailable" sitting under a map
    // that went on to draw perfectly well.
    tiles.on('tileload', () => {
      hasLoadedATile = true;
      setUnavailable(false);
    });
    // No tile having EVER loaded means the provider or the account is down,
    // not that one grid square happens to have no imagery — a stray failure
    // once the map is working is left alone rather than tearing it down.
    tiles.on('tileerror', () => {
      if (!hasLoadedATile) {
        setUnavailable(true);
      }
    });
    tiles.on('tileunload', (event: L.TileEvent) => {
      const tile = event.tile as TileImage;
      if (tile.blobUrl) {
        URL.revokeObjectURL(tile.blobUrl);
      }
      tile.abort?.abort();
    });
    tiles.addTo(map);

    const marker = L.marker([latitude, longitude], {
      icon: L.divIcon({ html: PIN_SVG, className: '', iconSize: [28, 28], iconAnchor: [14, 26] }),
      draggable: editable,
      alt: tRef.current('propertyLocationMap.markerAlt'),
      // Focusable when it can be moved, so the keyboard path below has
      // something to receive key events on.
      keyboard: editable,
    });
    marker.addTo(map);

    if (editable) {
      const save = () => {
        const onCorrectNow = onCorrectRef.current;
        if (!onCorrectNow) {
          return;
        }
        const position = marker.getLatLng();
        setSaveError(false);
        setSaving(true);
        onCorrectNow(position.lat, position.lng)
          .catch(() => {
            marker.setLatLng([latitude, longitude]);
            setSaveError(true);
          })
          .finally(() => setSaving(false));
      };

      marker.on('dragend', save);

      // The keyboard half of the same action (web/CONVENTIONS.md: no
      // mouse-only paths). Arrows nudge the pin, Enter commits it, Escape puts
      // it back — a drag is a continuous gesture, so it gets an explicit
      // commit rather than one save per keypress.
      const element = marker.getElement();
      element?.setAttribute('aria-label', tRef.current('propertyLocationMap.markerAlt'));
      element?.addEventListener('keydown', (event: KeyboardEvent) => {
        const step = event.shiftKey ? NUDGE_DEGREES * 10 : NUDGE_DEGREES;
        const at = marker.getLatLng();
        switch (event.key) {
          case 'ArrowUp':
            marker.setLatLng([at.lat + step, at.lng]);
            break;
          case 'ArrowDown':
            marker.setLatLng([at.lat - step, at.lng]);
            break;
          case 'ArrowLeft':
            marker.setLatLng([at.lat, at.lng - step]);
            break;
          case 'ArrowRight':
            marker.setLatLng([at.lat, at.lng + step]);
            break;
          case 'Enter':
          case ' ':
            save();
            break;
          case 'Escape':
            marker.setLatLng([latitude, longitude]);
            break;
          default:
            return;
        }
        event.preventDefault();
      });
    }

    return () => {
      map.remove();
    };
    // Recreated on a real coordinate/mode change only — `onCorrect` and `t`
    // are deliberately absent, read through refs above. A drag in progress
    // never reaches here because the marker's own position is Leaflet-internal
    // until `dragend` reports it back up.
  }, [latitude, longitude, editable, compact, zoom]);

  return (
    <div className="mt-3">
      {/* `img` only for the compact mini-map, which has no controls inside it.
          Leaflet mounts its zoom buttons INTO this element, and role="img"
          makes every descendant presentational — the buttons would stay
          keyboard-focusable while disappearing from the accessibility tree. */}
      <div
        ref={containerRef}
        role={compact ? 'img' : 'region'}
        aria-label={t('propertyLocationMap.mapLabel')}
        className={compact ? 'h-32 rounded-md border' : 'h-64 rounded-lg border'}
        data-testid="property-location-map"
      />
      {unavailable ? (
        <p
          className="mt-2 text-sm text-[var(--color-muted-foreground)]"
          data-testid="property-location-map-unavailable"
        >
          {t('propertyLocationMap.unavailable')}
        </p>
      ) : null}
      {editable ? (
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]" role="status">
          {saving ? t('propertyLocationMap.saving') : t('propertyLocationMap.dragHint')}
        </p>
      ) : null}
      {saveError ? (
        <p role="alert" className="mt-1 text-sm text-[var(--color-destructive)]">
          {t('propertyLocationMap.saveError')}
        </p>
      ) : null}
    </div>
  );
}
